import { useCallback, useRef, useState } from "react";

import { bytesPerMegaByte } from "@smart/bridge-types-basic";
import {
  FileFieldItem,
  FileInputField,
  FileUpload,
  Modal,
  UploadFileProps,
  UploadStatus,
} from "@smart/itops-sb-design-system-dom";
import {
  checkIsPdfEncrypted,
  checkIsWordDocEncrypted,
  unique,
  v4,
} from "@smart/itops-utils-basic";

import { ErrorDisplay } from "./error";
import { Label } from "./label";
import { FieldProps, FieldWrapper } from "./wrapper";
import { fieldName } from "../../hooks";
import { LookupOptions } from "../../types";
import {
  buildFileUpdateSizeLimitText,
  buildFileUploadPrompt,
} from "../../utils";

const defaultMaxNumOfFiles = 5;
const defaultFileSizeLimitMegaByte = 128;

export type FileFieldValue = {
  fileName: string;
  uploadStatus: UploadStatus;
  key?: string;
};

type FileFieldEntry = FileFieldValue & {
  file?: File;
  uploadUrl?: string;
  fileId: string;
};

export const applyFileLoadResults = ({
  files,
  pendingUploads,
  loadResults,
  invalidStatuses,
}: {
  files: FileFieldEntry[];
  pendingUploads: { fileId: string; fileName: string }[];
  loadResults: { key?: string; uploadUrl?: string }[] | undefined;
  invalidStatuses: UploadStatus[];
}): FileFieldEntry[] => {
  const resultIndexByFileId = new Map(
    pendingUploads.map(({ fileId }, i) => [fileId, i]),
  );

  return files.map((fileValue) => {
    const resultIndex = resultIndexByFileId.get(fileValue.fileId);
    if (resultIndex === undefined) return fileValue;

    const fileResult = loadResults?.[resultIndex];
    return {
      ...fileValue,
      uploadUrl: fileResult?.uploadUrl || "",
      uploadStatus:
        !fileResult?.uploadUrl &&
        !invalidStatuses.includes(fileValue.uploadStatus)
          ? "failedToUpload"
          : fileValue.uploadStatus,
      key: fileResult?.key,
    };
  });
};

const normaliseFileFieldValue = (
  value: FileFieldValue[] | undefined,
): FileFieldValue[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  // For backwards compatibility
  return [
    {
      ...(value as unknown as Omit<FileFieldValue, "uploadStatus">),
      uploadStatus: "uploaded" as const,
    },
  ];
};

export const FileField = ({
  field,
  index,
  innerRef,
  value,
  error,
  loading,
  disabled,
  hideLabel,
  onChange,
  onBlur,
  fileLookup: { load, upload },
  uploadFileProps,
  maxNumOfFiles,
  fileSizeLimitMegaByte,
  supportedFileTypes,
  selectedLanguage,
}: FieldProps<HTMLInputElement, FileFieldValue[]> & {
  fileLookup: LookupOptions["fileLookup"];
  maxNumOfFiles?: number;
  fileSizeLimitMegaByte?: number;
  supportedFileTypes?: { name: string; extension: string }[];
} & { uploadFileProps: UploadFileProps }) => {
  const maxFilesModal = useState(false);
  const [exceedMaxFiles, setExceedMaxFiles] = useState(false);
  const maxNumOfFilesToUse = maxNumOfFiles || defaultMaxNumOfFiles;
  const fileSizeLimitToUse =
    fileSizeLimitMegaByte || defaultFileSizeLimitMegaByte;
  const fileTypeNames = supportedFileTypes?.length
    ? unique(supportedFileTypes.map((f) => f.name))
    : [];
  const sizeLimit = buildFileUpdateSizeLimitText(
    {
      maxFiles: maxNumOfFilesToUse,
      fileTypeNames,
      sizeLimit: fileSizeLimitToUse,
    },
    selectedLanguage,
  );
  const uploadPrompt = buildFileUploadPrompt(
    maxNumOfFilesToUse > 1,
    selectedLanguage,
  );
  const acceptedFileTypes = supportedFileTypes?.length
    ? supportedFileTypes.map((f) => f.extension).join(",")
    : undefined;
  const invalidStatuses: UploadStatus[] = [
    "exceedSizeLimit",
    "failedToUpload",
    "unsupportedFileType",
  ];

  // We use a ref to keep the latest files instead of relaying on the value passed in
  // so we can update (e.g changing uploadStatus) in between rendering cycles and not get outdated data.
  const filesRef = useRef<
    (FileFieldValue & { file?: File; uploadUrl?: string; fileId: string })[]
  >(
    normaliseFileFieldValue(value).map((fileValue) => ({
      ...fileValue,
      fileId: v4(),
    })),
  );

  const fieldWrapperRef = useRef<HTMLDivElement | null>(null);

  const scrollIntoFieldWrapper = () => {
    const fieldWrapperElement = fieldWrapperRef.current;
    if (!fieldWrapperElement) return;

    const headerHeight =
      document
        .querySelector('[data-testid="heading-wrapper"]')
        ?.getBoundingClientRect().height ?? 0;

    const scrollParent =
      document.querySelector('[data-testid="page-wrapper"]') ||
      document.scrollingElement ||
      document.documentElement;

    const offsetTopInParent =
      fieldWrapperElement.getBoundingClientRect().top -
      scrollParent.getBoundingClientRect().top +
      scrollParent.scrollTop;

    const target = Math.max(0, offsetTopInParent - headerHeight);

    scrollParent.scrollTo({ top: target, behavior: "smooth" });
  };

  const updateFieldValue = () =>
    onChange(
      filesRef.current.map(({ fileName, uploadStatus, key }) => ({
        fileName,
        uploadStatus,
        key,
      })),
    );

  const removeInvalidFiles = () => {
    filesRef.current = filesRef.current.filter(
      ({ uploadStatus }) => !invalidStatuses.includes(uploadStatus),
    );
  };

  const validateFile = async (
    file: File,
  ): Promise<UploadStatus | undefined> => {
    if (file.size > fileSizeLimitToUse * bytesPerMegaByte)
      return "exceedSizeLimit";

    const extension = file.name.split(".").pop() || "";
    if (
      acceptedFileTypes &&
      !acceptedFileTypes.includes(`.${extension.toLowerCase()}`)
    ) {
      return "unsupportedFileType";
    }

    const checkIsFileEncrypted = async () => {
      switch (extension?.toLowerCase()) {
        case "pdf":
          return checkIsPdfEncrypted(file);
        case "doc":
        case "docx":
          return checkIsWordDocEncrypted(file);
        default:
          return false;
      }
    };
    if (await checkIsFileEncrypted()) {
      return "fileEncrypted";
    }

    return undefined;
  };

  const addFiles = async (files: File[]) => {
    filesRef.current = [
      ...filesRef.current,
      ...(await Promise.all(
        files.map(async (file) => ({
          fileId: v4(),
          fileName: file.name,
          uploadUrl: "",
          uploadStatus: (await validateFile(file)) || ("notUploaded" as const),
          file,
        })),
      )),
    ];
    updateFieldValue();
  };

  const id = fieldName({ field, index });
  const errorId = fieldName({ field, index, suffix: "error" });

  const onSelect = useCallback(
    async (fileList?: FileList | null) => {
      if (!fileList?.length) return;

      removeInvalidFiles();

      if (filesRef.current.length >= maxNumOfFilesToUse) {
        maxFilesModal[1](true);
        return;
      }
      const files = Array.from(fileList);
      const filesToAdd =
        filesRef.current.length + files.length > maxNumOfFilesToUse
          ? files.slice(0, maxNumOfFilesToUse - filesRef.current.length)
          : files;
      if (filesToAdd.length < files.length) setExceedMaxFiles(true);

      await addFiles(filesToAdd);

      const pendingUploads = filesRef.current
        .slice(-filesToAdd.length)
        .map(({ fileId, fileName }) => ({ fileId, fileName }));  

      let result: Awaited<ReturnType<typeof load>>;
      try {
        result = await load({
          fileNames: filesToAdd.map((f) => f.name),
          fieldUri: field.uri,
        });
        scrollIntoFieldWrapper();
      } catch (e) {
        console.error(e);
      }

      filesRef.current = applyFileLoadResults({
        files: filesRef.current,
        pendingUploads,
        loadResults: result,
        invalidStatuses,
      });

      updateFieldValue();
      onBlur();
    },
    [field.uri],
  );

  const getUploadedFiles = () => {
    if (!value) return null;
    const uploadedFiles = normaliseFileFieldValue(value);
    if (!uploadedFiles.length) return null;

    return (
      <div className="flex flex-col gap-small mt-[1.6rem]">
        {uploadedFiles.map((file, i) => {
          // filesRef.current is always kept index-aligned with value, but a
          // stable per-file id (not array position) is what identifies this
          // file for the rest of its lifetime -- a sibling being removed
          // must never change which upload/progress state this file reads
          // from or writes to.
          const fileId = filesRef.current[i]?.fileId || `fallback-${i}`;

          return (
            <FileFieldItem
              key={fileId}
              fileName={file.fileName}
              uploadUrl={filesRef.current[i]?.uploadUrl}
              file={filesRef.current[i]?.file}
              uploadStatus={file.uploadStatus}
              upload={upload}
              fileId={fileId}
              fieldId={id}
              uploadFileProps={uploadFileProps}
              fileSizeLimitMegaByte={fileSizeLimitToUse}
              onStatusChange={(status: UploadStatus) => {
                filesRef.current = filesRef.current.map((f) =>
                  f.fileId === fileId ? { ...f, uploadStatus: status } : f,
                );
                updateFieldValue();
                onBlur();
              }}
              onRemove={() => {
                filesRef.current = filesRef.current.filter(
                  (f) => f.fileId !== fileId,
                );
                uploadFileProps.remove(id, fileId);
                setExceedMaxFiles(false);
                updateFieldValue();
                onBlur();
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <FieldWrapper disabled={disabled} ref={fieldWrapperRef} isLoading={loading}>
      <label htmlFor={id}>
        <Label {...field} hideLabel={hideLabel} />
      </label>
      <FileUpload
        className="mt-[0.8rem]"
        id={id}
        error={error}
        errorId={errorId}
        onSelect={onSelect}
        sizeLimit={sizeLimit}
        disabled={disabled}
        uploadPrompt={uploadPrompt}
      />
      <ErrorDisplay
        className="mt-[0.5rem]"
        uri={field.uri}
        index={index}
        error={
          error ||
          (exceedMaxFiles
            ? "Maximum upload limit reached. Only 5 files were added."
            : undefined)
        }
      />
      {getUploadedFiles()}
      <FileInputField
        className="file-value"
        ref={innerRef}
        id={id}
        name={id}
        aria-invalid={!!error}
        aria-errormessage={error ? errorId : undefined}
        type="file"
        data-testid="file-upload"
        data-voice-file-field={field.uri}
        multiple
        accept={acceptedFileTypes}
        onChange={async (e) => {
          e.preventDefault();
          onSelect(e.target.files).catch(console.error);
        }}
      />
      <Modal
        header={{
          text: "Upload Limit Reached",
          icon: "solidFileArrowUp",
          hideIconOnMobile: true,
        }}
        open={maxFilesModal[0]}
        onClose={() => maxFilesModal[1](false)}
        footer={{
          buttons: [
            {
              text: "Ok",
              type: "button",
              onClick: () => maxFilesModal[1](false),
            },
          ],
        }}
        closeOptions={{
          escapeKey: false,
          clickOutside: false,
        }}
      >
        <p>
          You can upload a maximum of 5 files. Please remove some to add new
          ones.
        </p>
      </Modal>
    </FieldWrapper>
  );
};
